// frontend/src/pages/Dashboard.js
import React, { useState, useMemo, useEffect } from 'react';
import { Landmark, Filter, AlertTriangle, Clock, Calendar as CalendarIcon, X, CheckCircle, Loader2 } from 'lucide-react'; 
import { PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, Tooltip, Legend, ResponsiveContainer, XAxis, YAxis } from 'recharts';

// --- MODAL: EFETIVAR AGENDAMENTO (PAGA O AGENDAMENTO E CRIA A FATURA/LANÇAMENTO) ---
const EfetivarAgendamentoModal = ({ schedule, accounts, formatCurrency, selectedCurrency, onClose, onSave, apiFetch }) => {
    const [loading, setLoading] = useState(false);
    const [accountId, setAccountId] = useState('');
    const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().split('T')[0]);
    const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0]);
    const [actualAmount, setActualAmount] = useState(schedule.amount);
    // NOVO ESTADO: Número da NF
    const [nfNumero, setNfNumero] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!accountId) return alert('Selecione uma conta para debitar o pagamento.');

        const numAmount = parseFloat(actualAmount);
        if (isNaN(numAmount) || numAmount <= 0) {
            return alert('Informe um valor válido e maior que zero para o pagamento.');
        }

        setLoading(true);
        try {
            // VERIFICA O CAMINHO: Fornecedor Cadastrado ou Nome Livre?
            if (schedule.supplier_id) {
                // CAMINHO 1: FORNECEDOR CADASTRADO -> Cria Contas a Pagar Completo
                const faturaData = {
                    fatura: {
                        fornecedor_id: schedule.supplier_id,
                        nf_numero: nfNumero || 'AGENDA', // Agora envia a NF preenchida
                        data_emissao: dataEmissao,
                        valor_total: numAmount,
                        observacoes: `Gerado pela Agenda: ${schedule.description}`,
                        linked_schedule_id: schedule.id
                    },
                    parcelas: [{
                        numero_parcela: 1,
                        data_vencimento: schedule.instanceDate,
                        valor_parcela: numAmount,
                        meio_pagamento: 'pix', 
                        pix_chave: null, boleto_numero: null, boleto_cod_barras: null
                    }]
                };

                const faturaResponse = await apiFetch('/contas-a-pagar', { method: 'POST', body: faturaData });
                
                const faturaCriada = await apiFetch(`/contas-a-pagar/fatura/${faturaResponse.id}`);
                const parcelaId = faturaCriada.parcelas[0].id;

                await apiFetch('/contas-a-pagar/pagar', {
                    method: 'POST',
                    body: {
                        parcela_ids: [parcelaId],
                        account_id: parseInt(accountId, 10),
                        data_pagamento: dataPagamento,
                        valor_pago_total: numAmount,
                        desconto_total: 0,
                        juros_total: 0,
                        custom_description: `PG FAT (AGENDA): ${schedule.supplier_name} - ${schedule.description}` 
                    }
                });

            } else {
                // CAMINHO 2: NOME LIVRE -> Cria Despesa Direta no Livro Caixa e dá baixa na Agenda
                
                // Formata a descrição customizada com a NF, se houver
                const descNf = nfNumero ? ` NF: ${nfNumero} -` : '';
                const txDescription = `PG FAT (AGENDA -${descNf} ${schedule.supplier_name}): ${schedule.description}`;

                // Envia direto para a rota de transações
                await apiFetch('/transactions', {
                    method: 'POST',
                    body: {
                        description: txDescription,
                        amount: numAmount,
                        date: dataPagamento,
                        type: 'expense',
                        category: schedule.category_name || 'Pagamento Fornecedor',
                        accountId: parseInt(accountId, 10)
                    }
                });

                // Atualiza o status da agenda simulando um pagamento fake apenas para ela não aparecer mais
                // Para não quebrar a lógica de "paidScheduleInstances" do Dashboard, precisamos criar uma "baixa" no banco
                // Como não temos Contas a Pagar, vamos apenas criar um log ou marcar o schedule como concluído se for recorrência 'none'
                
                // Nota: O ideal para uma agenda sem Contas a Pagar seria criar uma tabela de "baixas manuais da agenda", mas 
                // podemos resolver isso marcando o schedule inteiro como 'completed' temporariamente ou se for única.
                if (schedule.recurrence === 'none') {
                    await apiFetch(`/contas-a-pagar/schedule/${schedule.id}`, {
                        method: 'PUT',
                        body: {
                            ...schedule,
                            status: 'completed'
                        }
                    });
                } else {
                     // Como a agenda repete, não podemos marcá-la toda como concluída e não temos parcela de Contas a Pagar para criar o elo 'ID_DATA'.
                     // Em um fluxo futuro mais complexo, o ideal seria o Backend registrar as datas de uma agenda livre que já foram pagas.
                     // Por hora, alertamos o usuário.
                     alert("Lançamento livre criado no Livro Caixa! Note que para recorrências (mensal/anual) sem Fornecedor Cadastrado, o card da agenda continuará pendente no calendário do Dashboard neste dia. Recomenda-se excluir a programação ou vincular a um fornecedor.");
                }
            }

            onSave();
        } catch (err) {
            alert(`Erro ao processar agendamento: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4 animate-in fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="bg-indigo-600 p-4 text-white flex justify-between items-center">
                    <h2 className="text-xl font-bold flex items-center gap-2"><CheckCircle /> Efetivar Agendamento</h2>
                    <button onClick={onClose} className="hover:bg-indigo-700 p-1 rounded-full"><X size={20}/></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-sm text-gray-500 uppercase font-bold">Fornecedor / Beneficiário</p>
                                <p className="text-lg font-semibold text-gray-900">{schedule.supplier_name}</p>
                                {!schedule.supplier_id && (
                                    <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded uppercase mt-1 inline-block">
                                        Não Cadastrado (Lançamento Direto)
                                    </span>
                                )}
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-500 uppercase font-bold">Categoria</p>
                                <p className="text-sm font-semibold text-indigo-600">{schedule.category_name || 'Geral'}</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-2 border-t pt-2">{schedule.description}</p>
                        
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                     Valor a Pagar (Editável)
                                 </label>
                                 <div className="relative w-full">
                                     <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 font-bold">
                                         {selectedCurrency === 'BRL' ? 'R$' : selectedCurrency}
                                     </span>
                                     <input 
                                         type="number" 
                                         step="0.01" 
                                         value={actualAmount} 
                                         onChange={e => setActualAmount(e.target.value)} 
                                         required 
                                         className="w-full pl-10 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 text-lg font-black text-indigo-700 outline-none"
                                     />
                                 </div>
                            </div>
                            <div>
                                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                     Nº da NF (Opcional)
                                 </label>
                                 <input 
                                     type="text" 
                                     value={nfNumero} 
                                     onChange={e => setNfNumero(e.target.value)} 
                                     placeholder="Ex: 123456"
                                     className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 text-lg font-medium text-gray-700 outline-none"
                                 />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Data de Emissão (NF)</label>
                            <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} required className="mt-1 w-full p-2 border rounded-md outline-none focus:border-indigo-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Data do Pagamento Real</label>
                            <input type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)} required className="mt-1 w-full p-2 border rounded-md outline-none focus:border-indigo-500" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Conta de Débito (Para baixar o valor)</label>
                        <select value={accountId} onChange={e => setAccountId(e.target.value)} required className="mt-1 w-full p-2 border rounded-md focus:ring-indigo-500 outline-none">
                            <option value="">Selecione uma conta ativa...</option>
                            {accounts.filter(a => a.status === 'ativo' && a.currency === selectedCurrency).map(a => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mt-6 pt-4 border-t flex flex-col sm:flex-row justify-end gap-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 font-bold rounded-lg hover:bg-gray-300 text-gray-700 transition-colors">Cancelar</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-indigo-600 font-bold text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2 transition-colors">
                            {loading ? <Loader2 size={18} className="animate-spin"/> : 'Criar Lançamento e Pagar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL DO DASHBOARD ---
const Dashboard = ({ transactions, accounts, accountTypes, currentUser, apiFetch, selectedCurrency, formatCurrency }) => {
    
    const [dashboardMonth, setDashboardMonth] = useState(new Date().getMonth() + 1);
    const [dashboardYear, setDashboardYear] = useState(new Date().getFullYear());
    const [dashboardUserId, setDashboardUserId] = useState('');
    const [historicalPeriod, setHistoricalPeriod] = useState(6);
    const [users, setUsers] = useState([]);
    
    // Novos Estados: Resumo Recebimentos e Agenda
    const [receivablesSummary, setReceivablesSummary] = useState({ vencidos: 0, aVencer: 0, countVencidos: 0, countAVencer: 0 });
    
    // --- ESTADOS DA NOVA AGENDA INTELIGENTE ---
    const [schedules, setSchedules] = useState([]);
    const [paidScheduleInstances, setPaidScheduleInstances] = useState(new Map());
    const [selectedSchedule, setSelectedSchedule] = useState(null);

    // CORREÇÃO: Verifica se é super usuário OU se tem permissão manageUsers
    const isAdmin = currentUser?.is_super_user || currentUser?.permissions?.manageUsers;

    // Busca Usuários e Dados Extras
    useEffect(() => {
        if (isAdmin) {
            apiFetch(`/users`)
                .then(setUsers)
                .catch(err => console.error("Falha ao buscar utilizadores para o dashboard:", err));
        }

        // Busca o resumo de recebimentos vencidos e a vencer
        const fetchReceivables = async () => {
            try {
                const resVencidos = await apiFetch('/reports/energy/receivables?viewType=vencidos&groupBy=list');
                const resAVencer = await apiFetch('/reports/energy/receivables?viewType=vencer&groupBy=list');
                
                const calcTotal = (data) => Array.isArray(data) ? data.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) : 0;
                
                setReceivablesSummary({
                    vencidos: calcTotal(resVencidos?.data),
                    countVencidos: Array.isArray(resVencidos?.data) ? resVencidos.data.length : 0,
                    aVencer: calcTotal(resAVencer?.data),
                    countAVencer: Array.isArray(resAVencer?.data) ? resAVencer.data.length : 0
                });
            } catch (err) { console.error("Erro ao buscar resumo de recebimentos", err); }
        };

        // Busca a agenda de pagamentos do mês atual
        const fetchSchedules = async () => {
            try {
                // 1. Busca todas as regras de agendamento do banco de dados (limit=1000 para vir tudo)
                const schedulesRes = await apiFetch(`/contas-a-pagar/schedule?limit=1000`);
                
                // 2. Busca todas as faturas do contas a pagar para sabermos quais já foram pagas!
                const payablesRes = await apiFetch(`/contas-a-pagar?limit=2000`);
                
                // 3. Monta um dicionário (Map) para guardar não apenas SE foi pago, mas QUAL VALOR foi pago
                const paidInstances = new Map();
                if (payablesRes.faturas) {
                    payablesRes.faturas.forEach(f => {
                        if (f.linked_schedule_id) {
                            f.parcelas.forEach(p => {
                                // Guarda a chave (ID_DATA) e o valor pago da parcela
                                paidInstances.set(`${f.linked_schedule_id}_${p.data_vencimento}`, p.valor_parcela);
                            });
                        }
                    });
                }
                
                if (schedulesRes && schedulesRes.schedules) {
                    setSchedules(schedulesRes.schedules);
                    setPaidScheduleInstances(paidInstances);
                }
            } catch (err) { console.error("Erro ao buscar agenda inteligente", err); }
        };

        fetchReceivables();
        fetchSchedules();
    }, [isAdmin, apiFetch, currentUser.entityId, dashboardMonth, dashboardYear]);
    
    // --- LÓGICA EXISTENTE DO DASHBOARD (MANTIDA) ---
    const filteredTransactions = useMemo(() => {
        return transactions.filter(t => {
            const transactionDate = new Date(t.date + 'T00:00:00');
            const matchesUser = !dashboardUserId || t.user_id === Number(dashboardUserId);
            const matchesMonth = transactionDate.getMonth() + 1 === Number(dashboardMonth);
            const matchesYear = transactionDate.getFullYear() === Number(dashboardYear);
            return matchesUser && matchesMonth && matchesYear;
        });
    }, [transactions, dashboardMonth, dashboardYear, dashboardUserId]);

    const { totalBalance, totalRevenue, totalExpenses, categorySpending } = useMemo(() => {
        let monthlyRevenue = 0;
        let monthlyExpenses = 0;
        const monthlyCategorySpending = {};

        filteredTransactions.forEach(t => {
            if (t.type === 'revenue' && t.category !== 'Transferência' && t.category !== 'Pagamento de Fatura') {
                monthlyRevenue += t.amount;
            } else if (t.type === 'expense' && t.category !== 'Transferência') {
                monthlyExpenses += t.amount;
                if (t.category) {
                    monthlyCategorySpending[t.category] = (monthlyCategorySpending[t.category] || 0) + t.amount;
                }
            } else if (t.type === 'bill_payment') { 
                 monthlyExpenses += t.amount;
                 monthlyCategorySpending['Pagamento de Fatura'] = (monthlyCategorySpending['Pagamento de Fatura'] || 0) + t.amount;
            }
        });

        const accountTypesById = new Map(accountTypes.map(type => [type.id, type]));
        const currentTotalBalance = accounts
            .filter(acc => acc.currency === selectedCurrency)
            .reduce((acc, account) => {
                const typeInfo = accountTypesById.get(account.account_type_id);
                if (typeInfo) {
                    const balanceField = typeInfo.fields.find(f => f.debits || f.credits);
                    if (balanceField) {
                        return acc + (parseFloat(account.customFields[balanceField.name]) || 0);
                    }
                }
                return acc;
        }, 0);

        return { 
            totalBalance: currentTotalBalance, 
            totalRevenue: monthlyRevenue, 
            totalExpenses: monthlyExpenses, 
            categorySpending: monthlyCategorySpending 
        };
    }, [filteredTransactions, accounts, accountTypes, selectedCurrency]);

    const individualAccountBalances = useMemo(() => {
        const accountTypesById = new Map(accountTypes.map(type => [type.id, type]));
        return accounts
            .filter(acc => acc.currency === selectedCurrency && acc.status === 'ativo')
            .map(account => {
                const typeInfo = accountTypesById.get(account.account_type_id);
                let balance = 0;
                if (typeInfo) {
                    const balanceField = typeInfo.fields.find(f => f.debits || f.credits || f.rec_pg_fatura);
                    if (balanceField) {
                        balance = parseFloat(account.customFields[balanceField.name] || 0);
                    }
                }
                return {
                    id: account.id,
                    name: account.name,
                    type: account.type,
                    balanceFormatted: formatCurrency(balance, selectedCurrency)
                };
            });
    }, [accounts, accountTypes, selectedCurrency, formatCurrency]);

    const categoryData = useMemo(() => {
        const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF19AF'];
        return Object.entries(categorySpending)
            .map(([name, value], index) => ({ name, value, fill: COLORS[index % COLORS.length] }))
            .sort((a, b) => b.value - a.value);
    }, [categorySpending]);

    const historicalData = useMemo(() => {
        const data = [];
        const now = new Date();
        for (let i = historicalPeriod - 1; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthName = date.toLocaleString('pt-BR', { month: 'short' });
            data.push({ name: monthName, Receitas: 0, Despesas: 0 });
        }
        
        transactions.forEach(t => {
            const transactionDate = new Date(t.date + 'T00:00:00');
            if (dashboardUserId && t.user_id !== Number(dashboardUserId)) return;

            const monthDiff = (now.getFullYear() - transactionDate.getFullYear()) * 12 + (now.getMonth() - transactionDate.getMonth());
            if (monthDiff >= 0 && monthDiff < historicalPeriod) {
                const index = historicalPeriod - 1 - monthDiff;
                if (t.type === 'revenue' && t.category !== 'Transferência') {
                    data[index].Receitas += t.amount;
                } else if ( (t.type === 'expense' && t.category !== 'Transferência') || t.type === 'bill_payment') {
                    data[index].Despesas += t.amount;
                }
            }
        });
        return data;
    }, [transactions, historicalPeriod, dashboardUserId]); 
    
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('pt-BR', { month: 'long' }) }));
    const yearOptions = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

    // --- LÓGICA DO CALENDÁRIO INTERATIVO INTELIGENTE ---
    const renderCalendarDays = () => {
        const year = Number(dashboardYear);
        const month = Number(dashboardMonth) - 1; 
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startingDayOfWeek = firstDayOfMonth.getDay(); 
        const daysInMonth = lastDayOfMonth.getDate();
        const today = new Date().toISOString().split('T')[0];

        let days = [];
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="p-2 border border-gray-100 bg-gray-50/50 min-h-[80px]"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const currentDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isToday = currentDateStr === today;

            // Busca os agendamentos extrapolando para este dia específico
            const daySchedules = schedules.filter(s => {
                const startDate = s.due_date;
                // Se a data de hoje for anterior ao "nascimento" da regra, não mostra.
                // OU se a regra for "none" e a data original já passou, não renderiza nos meses futuros.
                if (currentDateStr < startDate && s.recurrence !== 'none') return false; 
                
                if (s.recurrence === 'none') {
                    return startDate === currentDateStr;
                }
                if (s.recurrence === 'monthly') {
                    // Pega apenas o DIA do vencimento original e compara
                    return startDate.split('-')[2] === String(day).padStart(2, '0');
                }
                if (s.recurrence === 'yearly') {
                    // Compara DIA e MÊS
                    return startDate.substring(5) === currentDateStr.substring(5);
                }
                return false;
            });

            days.push(
                <div key={`day-${day}`} className={`p-2 border border-gray-200 min-h-[100px] flex flex-col ${isToday ? 'bg-blue-50 ring-inset ring-2 ring-blue-400' : 'bg-white'}`}>
                    <span className={`text-xs font-bold ${isToday ? 'text-blue-700' : 'text-gray-500'} mb-1`}>{day}</span>
                    <div className="flex-1 flex flex-col gap-1 overflow-y-auto">
                        {daySchedules.map(sch => {
                            // AQUI É A MÁGICA: Verifica se já existe uma Fatura e pega o valor pago
                            const instanceKey = `${sch.id}_${currentDateStr}`;
                            
                            // Se for livre (sem ID de fornecedor) e marcado como 'completed', a gente entende que foi pago
                            // (Embora em recorrências livres a gente não tenha o tracking por data, se for 'none' ele fica verde).
                            const isCompleted = paidScheduleInstances.has(instanceKey) || (sch.status === 'completed' && sch.recurrence === 'none');
                            
                            const actualPaidAmount = paidScheduleInstances.has(instanceKey) ? paidScheduleInstances.get(instanceKey) : sch.amount;
                            const isLateOrToday = !isCompleted && currentDateStr <= today;
                            
                            return (
                                <div 
                                    key={sch.id}
                                    onClick={() => { 
                                        if(!isCompleted) {
                                            // Passa o schedule MAS salva a data EXATA clicada
                                            setSelectedSchedule({ ...sch, instanceDate: currentDateStr });
                                        } 
                                    }}
                                    className={`text-[10px] p-1.5 rounded-md border cursor-pointer font-medium truncate shadow-sm transition-all
                                        ${isCompleted ? 'bg-green-100 border-green-300 text-green-800' : 
                                          isLateOrToday ? 'bg-red-100 border-red-400 text-red-800 animate-pulse hover:bg-red-200' : 
                                          'bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100'
                                        }
                                    `}
                                    title={`${sch.supplier_name} - ${sch.description} : ${formatCurrency(actualPaidAmount, selectedCurrency)}`}
                                >
                                    <div className="flex justify-between items-center gap-1 mb-0.5">
                                        <span className="truncate">{sch.supplier_name} - {sch.description}</span>
                                        {isCompleted ? <CheckCircle size={10} className="shrink-0"/> : <Clock size={10} className="shrink-0"/>}
                                    </div>
                                    <div className="font-bold">{formatCurrency(actualPaidAmount, selectedCurrency)}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return days;
    };

    return (
        <div className="p-2 md:p-6 pb-24 md:pb-6 relative"> 
            
            {/* Modal de Efetivação do Agendamento */}
            {selectedSchedule && (
                <EfetivarAgendamentoModal 
                    schedule={selectedSchedule}
                    accounts={accounts}
                    formatCurrency={formatCurrency}
                    selectedCurrency={selectedCurrency}
                    apiFetch={apiFetch}
                    onClose={() => setSelectedSchedule(null)}
                    onSave={() => {
                        setSelectedSchedule(null);
                        // Recarrega tudo para refletir a nova conta a pagar e o novo saldo
                        window.location.reload(); 
                    }}
                />
            )}

            {/* CABEÇALHO E FILTROS */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-3">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-gray-800">Dashboard <span className="text-sm font-normal text-gray-500">({selectedCurrency})</span></h1>
                    <p className="text-xs md:text-sm text-gray-600">Resumo financeiro da entidade.</p>
                </div>
                
                {/* AJUSTE MOBILE: Flex wrapping no mobile para que não esmague os filtros */}
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                    {isAdmin && (
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="userFilter" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5">Usuário</label>
                            <select id="userFilter" value={dashboardUserId} onChange={e => setDashboardUserId(e.target.value)} className="w-full p-1.5 md:p-2 text-xs md:text-sm border border-gray-300 rounded-md bg-white focus:ring-indigo-500">
                                <option value="">Todos</option>
                                {users.map(user => <option key={user.id} value={user.id}>{user.username}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="flex-1 min-w-[100px]">
                        <label htmlFor="monthFilter" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5">Mês</label>
                        <select id="monthFilter" value={dashboardMonth} onChange={e => setDashboardMonth(e.target.value)} className="w-full p-1.5 md:p-2 text-xs md:text-sm border border-gray-300 rounded-md bg-white focus:ring-indigo-500">
                            {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[80px]">
                        <label htmlFor="yearFilter" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5">Ano</label>
                        <select id="yearFilter" value={dashboardYear} onChange={e => setDashboardYear(e.target.value)} className="w-full p-1.5 md:p-2 text-xs md:text-sm border border-gray-300 rounded-md bg-white focus:ring-indigo-500">
                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>
            </div>
            
            {/* NOVOS CARDS DE RECEBIMENTOS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-red-800 mb-1">
                            <AlertTriangle size={18}/> <h3 className="font-bold uppercase text-xs tracking-wider">Recebimentos Vencidos</h3>
                        </div>
                        <p className="text-2xl font-black text-red-700">{formatCurrency(receivablesSummary.vencidos, selectedCurrency)}</p>
                        <p className="text-xs text-red-600 mt-1">{receivablesSummary.countVencidos} faturas atrasadas. <span className="font-bold underline cursor-help" title="Acesse o menu Contas a Receber ou Relatórios para ver os detalhes.">Ver detalhes no menu.</span></p>
                    </div>
                    <div className="p-3 bg-red-200 rounded-full text-red-600 opacity-50 hidden sm:block"><Landmark size={32}/></div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 shadow-sm flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-yellow-800 mb-1">
                            <Clock size={18}/> <h3 className="font-bold uppercase text-xs tracking-wider">Recebimentos A Vencer</h3>
                        </div>
                        <p className="text-2xl font-black text-yellow-700">{formatCurrency(receivablesSummary.aVencer, selectedCurrency)}</p>
                        <p className="text-xs text-yellow-600 mt-1">{receivablesSummary.countAVencer} faturas programadas. <span className="font-bold underline cursor-help" title="Acesse o menu Contas a Receber ou Relatórios para ver os detalhes.">Ver detalhes no menu.</span></p>
                    </div>
                    <div className="p-3 bg-yellow-200 rounded-full text-yellow-600 opacity-50 hidden sm:block"><CalendarIcon size={32}/></div>
                </div>
            </div>

            {/* CARDS DE RESUMO (EXISTENTES) */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mb-6">
                <div className="col-span-2 md:col-span-1 p-4 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center">
                    <h3 className="text-xs md:text-sm font-bold text-gray-500 uppercase tracking-wider">Saldo Total de Contas</h3>
                    <p className={`text-2xl md:text-3xl font-black mt-1 ${totalBalance >= 0 ? 'text-blue-800' : 'text-red-600'}`}>
                        {formatCurrency(totalBalance, selectedCurrency)}
                    </p>
                </div>
                <div className="col-span-1 p-3 md:p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-[10px] md:text-sm font-bold text-gray-500 uppercase tracking-wider">Receitas no Mês</h3>
                    <p className="text-lg md:text-3xl font-black text-green-600 mt-1 truncate">
                        {formatCurrency(totalRevenue, selectedCurrency)}
                    </p>
                </div>
                <div className="col-span-1 p-3 md:p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-[10px] md:text-sm font-bold text-gray-500 uppercase tracking-wider">Despesas no Mês</h3>
                    <p className="text-lg md:text-3xl font-black text-red-500 mt-1 truncate">
                        {formatCurrency(totalExpenses, selectedCurrency)}
                    </p>
                </div>
            </div>

            {/* NOVO: CALENDÁRIO DA AGENDA DE PAGAMENTOS COM RESPONSIVIDADE HORIZONTAL */}
            <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-indigo-600 p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center text-white gap-3">
                    <div className="flex items-center gap-2">
                        <CalendarIcon size={20} />
                        <h2 className="font-bold text-lg">Agenda de Pagamentos</h2>
                    </div>
                    <span className="text-xs font-medium bg-indigo-500 px-3 py-1 rounded-full border border-indigo-400">
                        {schedules.length} agendamentos base
                    </span>
                </div>
                
                {/* O container interno rola na horizontal no celular */}
                <div className="p-4 bg-gray-50 overflow-x-auto">
                    <p className="md:hidden text-xs text-indigo-600 font-medium text-center mb-3 animate-pulse">
                        Arraste para o lado para ver o calendário inteiro 👉
                    </p>
                    
                    {/* Força largura mínima para não esmagar os dias */}
                    <div className="min-w-[800px]">
                        {/* Cabeçalho dos Dias da Semana */}
                        <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-bold text-gray-500 uppercase">
                            <div>Dom</div><div>Seg</div><div>Ter</div><div>Qua</div><div>Qui</div><div>Sex</div><div>Sáb</div>
                        </div>
                        {/* Grid do Calendário */}
                        <div className="grid grid-cols-7 gap-2">
                            {renderCalendarDays()}
                        </div>
                    </div>
                    
                    {/* Legenda com wrap para não quebrar no celular */}
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-600 font-medium justify-center">
                        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-100 border border-red-400 rounded-full"></div> Atrasado / Pagar Hoje</span>
                        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-50 border border-yellow-300 rounded-full"></div> Futuro</span>
                        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-green-100 border border-green-300 rounded-full"></div> Concluído / Pago</span>
                    </div>
                </div>
            </div>

            {/* SALDOS POR CONTA */}
            {individualAccountBalances.length > 0 && (
                <div className="mb-6">
                    <h3 className="text-base md:text-xl font-bold text-gray-700 mb-3 px-1">Saldos por Conta</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
                        {individualAccountBalances.map(account => (
                            <div key={account.id} className="flex flex-col md:flex-row md:items-center p-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-2 mb-2 md:mb-0 md:mr-3">
                                    <div className="p-1.5 md:p-2 bg-blue-50 rounded-full shrink-0">
                                        <Landmark size={16} className="text-blue-600 md:w-5 md:h-5" />
                                    </div>
                                    {/* Nome da conta visível ao lado do ícone no mobile para economizar altura */}
                                    <p className="text-xs font-semibold text-gray-700 line-clamp-1 md:hidden">{account.name}</p>
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-sm font-semibold text-gray-800 hidden md:block truncate">{account.name}</p>
                                    <p className="text-sm md:text-lg font-bold text-gray-900 truncate">{account.balanceFormatted}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* GRÁFICOS */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                {/* Gráfico de Pizza */}
                <div className="p-4 md:p-6 bg-white rounded-lg shadow-sm border border-gray-100 lg:col-span-2">
                    <h3 className="font-bold text-gray-800 mb-4 text-sm md:text-base">Despesas por Categoria</h3>
                    <div className="h-[250px] md:h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie 
                                    data={categoryData} 
                                    dataKey="value" 
                                    nameKey="name" 
                                    cx="50%" 
                                    cy="50%" 
                                    outerRadius={80} 
                                    fill="#8884d8"
                                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                                        // Label simplificada para mobile
                                        if (percent < 0.05) return null; // Esconde se for muito pequeno
                                        const RADIAN = Math.PI / 180;
                                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                        return (
                                            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={10}>
                                                {`${(percent * 100).toFixed(0)}%`}
                                            </text>
                                        );
                                    }}
                                >
                                    {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value, selectedCurrency)} />
                                <Legend wrapperStyle={{ fontSize: '10px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico de Barras */}
                <div className="p-4 md:p-6 bg-white rounded-lg shadow-sm border border-gray-100 lg:col-span-3">
                     <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                        <h3 className="font-bold text-gray-800 text-sm md:text-base">Receitas vs. Despesas</h3>
                        <div className="flex items-center gap-1 md:gap-2">
                             {[6, 9, 12].map(period => (
                                <button 
                                    key={period} 
                                    onClick={() => setHistoricalPeriod(period)} 
                                    className={`px-2 py-1 text-[10px] md:text-xs rounded-md transition-colors ${historicalPeriod === period ? 'bg-indigo-600 text-white font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    {period === 12 ? '1 Ano' : `${period}M`}
                                </button>
                            ))}
                        </div>
                     </div>
                    <div className="h-[250px] md:h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={historicalData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                                <YAxis 
                                    tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value} 
                                    tick={{fontSize: 10}} 
                                    axisLine={false} 
                                    tickLine={false}
                                />
                                <Tooltip 
                                    formatter={(value) => formatCurrency(value, selectedCurrency)}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                />
                                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                                <Bar dataKey="Receitas" fill="#10B981" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="Despesas" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;